import { CameraIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { updateUser } from "../api/user.api";
import {
  createUserQualification,
  updateUserQualification,
} from "../api/userQualification.api";
import { UploadImageInput } from "../components/chat/upload-image-input";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import PhoneInput from "../components/shared/phone-input";
import ThemedSpinner from "../components/shared/spinner";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import { useAuth } from "../hooks/AuthContext";
import {
  FileUploadContextWrapper,
  useFileUploadContext,
} from "../hooks/FileUploadContext";
import { useToast } from "../hooks/ToastContext";
import useLoadUserQualifications from "../hooks/useLoadUserQualifications";
import useUserDetails from "../hooks/useUserDetails";

const AccountPageChild = () => {
  const { user, updatePassword } = useAuth();
  const { userDetails } = useUserDetails();
  const { qualifications } = useLoadUserQualifications();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [qualificationType, setQualificationType] = useState("medical_license");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [licenseCountry, setLicenseCountry] = useState("US");
  const [specialty, setSpecialty] = useState("");
  const [boardCertification, setBoardCertification] = useState("");
  const [qualificationNotes, setQualificationNotes] = useState("");

  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const {
    files,
    setFiles,
    fileUploadResponse,
    fileUploading,
    uploadFilesForChat,
  } = useFileUploadContext();

  useEffect(() => {
    if (userDetails) {
      setFirstName(userDetails.first_name || "");
      setLastName(userDetails.last_name || "");
      setPhone(userDetails.phone || "");
      setAvatarUrl(userDetails.avatar_url || undefined);
    }
  }, [userDetails]);

  useEffect(() => {
    if (qualifications && qualifications.length > 0) {
      const q = qualifications[0];
      setQualificationType(q.qualification_type || "");
      setLicenseNumber(q.license_number || "");
      setLicenseState(q.license_state || "");
      setLicenseCountry(q.license_country || "US");
      setSpecialty(q.specialty || "");
      setBoardCertification(q.board_certification || "");
      setQualificationNotes(q.notes || "");
    }
  }, [qualifications]);

  useEffect(() => {
    if (files.length > 0 && !fileUploading) {
      uploadFilesForChat({
        files,
        additionalData: { purpose: "profile_avatar" },
      });
    }
  }, [files, fileUploading, uploadFilesForChat]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", user?.id] });
      showToast("Account updated", "success");
    },
    onError: () => {
      showToast("Failed to update account", "error");
    },
  });

  const { mutate: saveQualification } = useMutation({
    mutationFn: (values: any) =>
      qualifications && qualifications.length > 0
        ? updateUserQualification(values)
        : createUserQualification(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["userQualifications", user?.id],
      });
      showToast("Certification saved", "success");
    },
    onError: () => {
      showToast("Failed to save certification", "error");
    },
  });

  useEffect(() => {
    if (fileUploadResponse && fileUploadResponse.length > 0 && userDetails) {
      const url = fileUploadResponse[0].url ?? null;
      setAvatarUrl(url ?? undefined);
      save({ id: userDetails.id, avatar_url: url ?? undefined });
      setFiles([]);
    }
  }, [fileUploadResponse, userDetails, save]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userDetails) return;
    save({
      id: userDetails.id,
      first_name: firstName,
      last_name: lastName,
      phone,
    });
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    setPasswordError(null);
    const success = await updatePassword(newPassword);
    if (success && userDetails && !userDetails?.is_registered) {
      save({
        id: userDetails.id,
      });
    }

    setNewPassword("");
    setConfirmPassword("");
  };

  const handleQualificationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userDetails) return;
    const payload: any = {
      user_id: userDetails.id,
      qualification_type: qualificationType,
      license_number: licenseNumber,
      license_state: licenseState,
      license_country: licenseCountry,
      specialty,
      board_certification: boardCertification,
      notes: qualificationNotes,
    };
    if (qualifications && qualifications.length > 0) {
      payload.id = qualifications[0].id;
    }
    saveQualification(payload);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Typography variant="h2" className="mb-8 text-terracotta">
        Account
      </Typography>
      <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
        <Card.Body>
          <div className="flex flex-col items-center gap-2 pb-4">
            {avatarUrl && (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-24 h-24 rounded-full object-cover"
              />
            )}
            {fileUploading && <ThemedSpinner size="sm" />}
            <UploadImageInput
              onUpload={(files) => setFiles(files)}
              accept=".jpg,.jpeg,.png,.gif,.webp"
              button={
                <button className="flex items-center gap-2">
                  <CameraIcon className="w-5 h-5 text-terracotta-500 transition-colors" />
                  <Typography variant="body1" className="text-terracotta">
                    {avatarUrl ? "Change Avatar" : "Add Avatar"}
                  </Typography>
                </button>
              }
            />
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TamaguiInput
              id="firstName"
              label="First Name"
              value={firstName}
              onChangeText={setFirstName}
              fullWidth
            />
            <TamaguiInput
              id="lastName"
              label="Last Name"
              value={lastName}
              onChangeText={setLastName}
              fullWidth
            />
            <PhoneInput
              id="phone"
              label="Phone"
              value={phone}
              onChangeText={setPhone}
              fullWidth
            />
            <TamaguiInput
              id="email"
              label="Email"
              value={user?.email || ""}
              disabled
              fullWidth
            />
            <Typography variant="body1" className="capitalize text-terracotta">
              Type: {userDetails?.type}
            </Typography>
            <SubmitButton
              isDisabled={isPending}
              onSubmit={handleSubmit}
              className="w-full"
            >
              Save
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>
      <Card
        className="w-full max-w-md mx-auto mt-8"
        variant="cream"
        elevation={2}
      >
        <Card.Body>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <TamaguiInput
              id="new-password"
              label={userDetails?.is_registered ? "New Password" : "Password"}
              value={newPassword}
              onChangeText={setNewPassword}
              fullWidth
              inputType="password"
            />
            <TamaguiInput
              id="confirm-password"
              label="Confirm Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              fullWidth
              inputType="password"
              error={passwordError || undefined}
            />
            <SubmitButton
              onSubmit={handlePasswordSubmit}
              isDisabled={!newPassword || !confirmPassword}
              className="w-full"
            >
              Update Password
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>
      <Card
        className="w-full max-w-md mx-auto mt-8"
        variant="cream"
        elevation={2}
      >
        <Card.Body>
          <form onSubmit={handleQualificationSubmit} className="space-y-4">
            <TamaguiInput
              id="qualification-type"
              label="Qualification Type"
              value={qualificationType}
              onChangeText={setQualificationType}
              fullWidth
            />
            <TamaguiInput
              id="license-number"
              label="License Number"
              value={licenseNumber}
              onChangeText={setLicenseNumber}
              fullWidth
            />
            <TamaguiInput
              id="license-state"
              label="License State"
              value={licenseState}
              onChangeText={setLicenseState}
              fullWidth
            />
            <TamaguiInput
              id="license-country"
              label="License Country"
              value={licenseCountry}
              onChangeText={setLicenseCountry}
              fullWidth
            />
            <TamaguiInput
              id="specialty"
              label="Specialty"
              value={specialty}
              onChangeText={setSpecialty}
              fullWidth
            />
            <TamaguiInput
              id="board-certification"
              label="Board Certification"
              value={boardCertification}
              onChangeText={setBoardCertification}
              fullWidth
            />
            <TamaguiInput
              id="qualification-notes"
              label="Notes"
              value={qualificationNotes}
              onChangeText={setQualificationNotes}
              fullWidth
            />
            <SubmitButton
              onSubmit={handleQualificationSubmit}
              className="w-full"
            >
              Save Certification
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default function AccountPage() {
  return (
    <FileUploadContextWrapper>
      <AccountPageChild />
    </FileUploadContextWrapper>
  );
}
